import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../services/api';

const DepartmentLedger = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [userData, setUserData] = useState({});
  const [error, setError] = useState(null);
  const [allocateLoading, setAllocateLoading] = useState(false);
  const [allocateSuccess, setAllocateSuccess] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  
  // Add states for user input
  const [allocationAmount, setAllocationAmount] = useState();
  const [allocationParticulars, setAllocationParticulars] = useState();

  // Search states
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  const [reportModalOpen, setReportModalOpen] = useState(false);
const [reportLoading, setReportLoading] = useState(false);
const [reportError, setReportError] = useState('');

const [reportFilters, setReportFilters] = useState({
  financialYear: '2026-27',
  districts: [],
  scheme: '',
  fromDate: '2026-04-01',
  toDate: '2027-03-31'
});

const [allocationFilters, setAllocationFilters] = useState({
  centerCode: '',
  scheme: '',
  financialYear: '2026-27',
  quarter: '1'
});

const [allocationView, setAllocationView] = useState({
  open: false,
  centerCode: '',
  centerName: '',
  centerType: '',
  scheme: '',
  amount: 0,
  summary: [],
  history: [],
  totals: {
    opening_balance: 0,
    added_amount: 0,
    used_amount: 0,
    closing_balance: 0
  }
});

const [allocationHistoryLoading, setAllocationHistoryLoading] = useState(false);
const [allocationHistoryError, setAllocationHistoryError] = useState('');

  // Get user role from localStorage
  const userRole = localStorage.getItem('userRole');

  // Define which schemes to show based on user role
  const getVisibleSchemes = () => {
    if (userRole === 'state') {
      // For state users, only show RGS, TCF, and GOI-MoRD
      return ['RGS', 'TCF', 'GOI-MoRD'];
    } else {
      // For other users, show all schemes
      return ['IDF', 'OUT', 'RGS', 'SFC', 'TCF', 'GOI-MoRD'];
    }
  };

  //const visibleSchemes = getVisibleSchemes();
  const visibleSchemes = getVisibleSchemes();

  const quarterOptions = [
    { value: '1', label: '1st Quarter - April, May, June' },
    { value: '2', label: '2nd Quarter - July, August, September' },
    { value: '3', label: '3rd Quarter - October, November, December' },
    { value: '4', label: '4th Quarter - January, February, March' }
  ];
  useEffect(() => {
    // Fetch balance data when component mounts
    fetchBalanceData();
  }, []);

  // Update current balance whenever transactions change
  useEffect(() => {
    if (transactions.length > 0) {
      // Get the latest balance from the first transaction (most recent)
      setCurrentBalance(parseFloat(transactions[0].balance));
    } else {
      setCurrentBalance(0);
    }
  }, [transactions]);

  // Function to get scheme balance by scheme name
  const getSchemeBalance = (detail, schemeName) => {
    if (!detail || !Array.isArray(detail)) return 0;
    
    try {
      const scheme = detail.find(s => {
        // Check if s exists and has a scheme property
        return s && typeof s === 'object' && s.scheme === schemeName;
      });
      
      return scheme && scheme.balance ? parseFloat(scheme.balance) : 0;
    } catch (error) {
      console.error('Error in getSchemeBalance:', error);
      return 0;
    }
  };

  // Function to fetch department balance data
  const fetchBalanceData = async () => {
    setLoading(true);
    try {
      const response = await api.ledger.getBalance();
      
      if (!response.error) {
        setBalanceData(response.data || []);
        setUserData(response.user || {});
      } else {
        setError(response.message || 'Failed to fetch balance data');
      }
    } catch (error) {
      console.error('Error fetching balance data:', error);
      setError('An error occurred while fetching balance data');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch transaction data for a specific department
  const fetchDepartmentTransactions = async (deptCode) => {
    setLoading(true);
    try {
      const response = await api.ledger.getDepartmentTransactions(deptCode);
      
      if (!response.error) {
        setTransactions(response.data || []);
        setSelectedDept(deptCode);
        
        // Update current balance if there are transactions
        if (response.data && response.data.length > 0) {
          setCurrentBalance(parseFloat(response.data[response.data.length - 1].balance));
        }
      } else {
        setError(response.message || 'Failed to fetch transaction data');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('An error occurred while fetching transaction data');
    } finally {
      setLoading(false);
    }
  };

const resetAllocationView = (open = false) => {
  setAllocationView({
    open,
    centerCode: '',
    centerName: '',
    centerType: '',
    scheme: '',
    amount: 0,
    summary: [],
    history: [],
    totals: {
      opening_balance: 0,
      added_amount: 0,
      used_amount: 0,
      closing_balance: 0
    }
  });
};

const openAllocationHistoryModal = () => {
  setAllocationHistoryError('');
  setAllocationFilters((prev) => ({
    ...prev,
    centerCode: '',
    scheme: ''
  }));
  resetAllocationView(true);
};

const closeAllocationHistoryModal = () => {
  setAllocationHistoryError('');
  resetAllocationView(false);
};

const clearAllocationHistoryResults = () => {
  setAllocationView((prev) => ({
    ...prev,
    centerCode: '',
    centerName: '',
    centerType: '',
    scheme: '',
    amount: 0,
    summary: [],
    history: [],
    totals: {
      opening_balance: 0,
      added_amount: 0,
      used_amount: 0,
      closing_balance: 0
    }
  }));
};

const fetchAllocationHistory = async (dept = null, scheme = '', filters = allocationFilters) => {
  const centerCode = dept?.type
    ? dept.type.toUpperCase()
    : (filters.centerCode || allocationView.centerCode || '').toUpperCase();

  const selectedDeptRecord = dept || balanceData.find(
    (item) => (item?.type || '').toUpperCase() === centerCode
  );

  const centerName = selectedDeptRecord?.name || allocationView.centerName || '';
  const centerType = selectedDeptRecord?.center || allocationView.centerType || '';
  const selectedScheme = scheme || filters.scheme || allocationView.scheme;

  if (!centerCode) {
    setAllocationHistoryError('Please select a Center.');
    return;
  }

  if (!selectedScheme) {
    setAllocationHistoryError('Please select a Scheme / Fund Type.');
    return;
  }

  setAllocationHistoryLoading(true);
  setAllocationHistoryError('');

  setAllocationView((prev) => ({
    ...prev,
    open: true,
    centerCode,
    centerName,
    centerType,
    scheme: selectedScheme,
    amount: selectedDeptRecord
      ? getSchemeBalance(selectedDeptRecord.detail, selectedScheme)
      : 0,
    summary: [],
    history: [],
    totals: {
      opening_balance: 0,
      added_amount: 0,
      used_amount: 0,
      closing_balance: 0
    }
  }));

  try {
    const params = new URLSearchParams({
      financialYear: filters.financialYear,
      quarter: filters.quarter,
      scheme: selectedScheme,
      centres: centerCode
    });

    const response = await fetch(
      `${api.BASE_URL}/ledger/allocation-history?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('authToken')
        }
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(data.message || 'Unable to load allocation history');
    }

    setAllocationView((prev) => ({
      ...prev,
      open: true,
      centerCode,
      centerName,
      centerType,
      scheme: selectedScheme,
      summary: data.summary || [],
      history: data.history || [],
      totals: data.totals || {
        opening_balance: 0,
        added_amount: 0,
        used_amount: 0,
        closing_balance: 0
      }
    }));
  } catch (error) {
    console.error('Allocation history error:', error);
    setAllocationHistoryError(error.message || 'Unable to load allocation history');
  } finally {
    setAllocationHistoryLoading(false);
  }
};

// Function to allocate funds to the selected department
const allocateFunds = async () => {
  if (!selectedDept || !allocationAmount || !allocationParticulars) return;
  
  setAllocateLoading(true);
  setAllocateSuccess(false);
  
  try {
    // Determine the department code to send to backend
    let deptCodeToSend = selectedDept;
    
    // If user is state or regional AND the selected scheme is GOI-MoRD, use SIR-GOI
    if ((userRole === 'state' || userRole === 'regional') && selectedDept.includes('GOI-MoRD')) {
      deptCodeToSend = 'SIR-GOI';
    }
    
    // Prepare fund allocation data
    const allocationData = {
      date: new Date().toISOString().split('T')[0],
      particulars: allocationParticulars,
      cr: allocationAmount
    };
    
    // Make API call to allocate funds
    const response = await fetch(`${api.BASE_URL}/ledger/${deptCodeToSend}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify(allocationData)
    });
    
    if (response.ok) {
      setAllocateSuccess(true);
      
      // Refresh both transaction list and department balances
      await fetchDepartmentTransactions(selectedDept);
      await fetchBalanceData();
      
      // Calculate new balance (current + allocated amount)
      setCurrentBalance(prev => prev + parseFloat(allocationAmount));
      
      // Reset the form
      setAllocationParticulars("");
      setAllocationAmount();
    } else {
      const errorData = await response.json();
      setError(errorData.message || 'Failed to allocate funds');
    }
  } catch (error) {
    console.error('Error allocating funds:', error);
    setError('An error occurred while allocating funds');
  } finally {
    setAllocateLoading(false);
  }
};

  // Filter departments based on search term
  const filteredDepartments = balanceData.filter(dept => {
    if (!departmentSearchTerm) return true;
    
    const searchTerm = departmentSearchTerm.toLowerCase();
    return (
      dept.type.toLowerCase().includes(searchTerm) ||
      (dept.name && dept.name.toLowerCase().includes(searchTerm)) ||
      (dept.center && dept.center.toLowerCase().includes(searchTerm))
    );
  });

  // Filter transactions based on search term and date range
  const filteredTransactions = transactions.filter(transaction => {
    // Text search filter
    if (transactionSearchTerm) {
      const searchTerm = transactionSearchTerm.toLowerCase();
      const matchesText = transaction.particulars.toLowerCase().includes(searchTerm);
      if (!matchesText) return false;
    }

    // Date range filter
    if (dateFilter.from || dateFilter.to) {
      const transactionDate = new Date(transaction.date);
      
      if (dateFilter.from) {
        const fromDate = new Date(dateFilter.from);
        if (transactionDate < fromDate) return false;
      }
      
      if (dateFilter.to) {
        const toDate = new Date(dateFilter.to);
        if (transactionDate > toDate) return false;
      }
    }

    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setDepartmentSearchTerm('');
    setTransactionSearchTerm('');
    setDateFilter({ from: '', to: '' });
  };

  const toggleReportDistrict = (code) => {
  setReportFilters(prev => {
    const exists = prev.districts.includes(code);

    return {
      ...prev,
      districts: exists
        ? prev.districts.filter(item => item !== code)
        : [...prev.districts, code]
    };
  });
};

const downloadLedgerReport = async () => {
  try {
    setReportLoading(true);
    setReportError('');

    const params = new URLSearchParams();

    params.set(
      'financialYear',
      reportFilters.financialYear
    );

    params.set(
      'fromDate',
      reportFilters.fromDate
    );

    params.set(
      'toDate',
      reportFilters.toDate
    );

    if (reportFilters.districts.length > 0) {
      params.set(
        'districts',
        reportFilters.districts.join(',')
      );
    }

    if (reportFilters.scheme) {
      params.set(
        'scheme',
        reportFilters.scheme
      );
    }

    const response = await fetch(
      `${api.BASE_URL}/ledger/download-report?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization:
            localStorage.getItem('authToken')
        }
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        data.message ||
        'Unable to generate report'
      );
    }

    const summaryRows = (data.summary || []).map(row => ({
      'Financial Year':
        reportFilters.financialYear,

      'District':
        row.district_name,

      'Center Code':
        row.center_code,

      'Scheme':
        row.scheme,

      'Allocated Amount':
        Number(row.allocated_amount || 0),

      'Expenditure':
        Number(row.expenditure_amount || 0),

      'Balance':
        Number(row.closing_balance || 0)
    }));

    const transactionRows =
      (data.transactions || []).map(row => ({
        'Financial Year':
          reportFilters.financialYear,

        'Date':
          row.date
            ? new Date(row.date)
                .toLocaleDateString('en-IN')
            : '',

        'District':
          row.district_name,

        'Center Code':
          row.center_code,

        'Scheme':
          row.scheme,

        'Particulars':
          row.particulars,

        'Allocated / Credit':
          Number(row.credit || 0),

        'Expenditure / Debit':
          Number(row.debit || 0),

        'Balance':
          Number(row.balance || 0)
      }));

    const workbook =
      XLSX.utils.book_new();

    const summarySheet =
      XLSX.utils.json_to_sheet(
        summaryRows
      );

    const transactionSheet =
      XLSX.utils.json_to_sheet(
        transactionRows
      );

    XLSX.utils.book_append_sheet(
      workbook,
      summarySheet,
      'Summary'
    );

    XLSX.utils.book_append_sheet(
      workbook,
      transactionSheet,
      'Transactions'
    );

    XLSX.writeFile(
      workbook,
      `Ledger_Report_${reportFilters.financialYear}.xlsx`
    );

  } catch (error) {

    console.error(error);

    setReportError(
      error.message ||
      'Unable to download report'
    );

  } finally {
    setReportLoading(false);
  }
};
  // Format date to readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
  const numericAmount = Number(amount || 0);

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(Number.isNaN(numericAmount) ? 0 : numericAmount);
};

  // Function to get scheme display name
  const getSchemeDisplayName = (scheme) => {
    // You can customize display names here if needed
    const displayNames = {
      'GOI-MoRD': 'GOI-MoRD',
      'RGS': 'RGS',
      'TCF': 'TCF',
      'IDF': 'IDF',
      'OUT': 'OUT',
      'SFC': 'SFC'
    };
    return displayNames[scheme] || scheme;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
        <p>{error}</p>
        <button 
          onClick={fetchBalanceData}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Simple attractive welcome header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg shadow-sm mb-4">
        <h2 className="text-2xl font-bold text-gray-800">
          Welcome, {userData.name}
        </h2>
        {userRole === 'state' && (
          <p className="text-sm text-gray-600 mt-1">
            Viewing scheme balances for: RGS, TCF, GOI-MoRD
          </p>
        )}
      </div>

      {/* Department Balance List */}
      {!selectedDept && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h3 className="text-lg font-medium text-gray-700">Ledger values for schemes</h3>
              
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                {/* One common History button for all schemes */}
                <button
                  type="button"
                  onClick={openAllocationHistoryModal}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  History
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReportError('');
                    setReportModalOpen(true);
                  }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                >
                  Download Report
                </button>
                {/* Department Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search departments..."
                    value={departmentSearchTerm}
                    onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg
                    className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {departmentSearchTerm && (
                  <button
                    onClick={() => setDepartmentSearchTerm('')}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {filteredDepartments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Center Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Center Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Center Type
                    </th>
                    {/* Dynamically render scheme columns based on user role */}
                    {visibleSchemes.map(scheme => (
                      <th 
                        key={scheme}
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {getSchemeDisplayName(scheme)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDepartments.map((dept, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {dept.type ? dept.type.toUpperCase() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {dept.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {dept.center || 'N/A'}
                      </td>
                      {/* Dynamically render scheme balance cells based on user role */}
                      {visibleSchemes.map(scheme => (
                        <td 
                          key={scheme}
                          className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600"
                        >
                          <button
                            onClick={() => dept.type && fetchDepartmentTransactions(`${dept.type}-${scheme}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline focus:outline-none"
                            title="View full transactions"
                          >
                            {formatCurrency(getSchemeBalance(dept.detail, scheme))}
                          </button>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              {departmentSearchTerm ? 
                `No departments found matching "${departmentSearchTerm}"` : 
                'No department balance data found.'
              }
            </div>
          )}
        </div>
      )}

      {/* Ledger Report Modal */}
      
      {reportModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setReportModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >

            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">

              <div>
                <h3 className="text-lg font-semibold">
                  Download Ledger Report
                </h3>

                <p className="text-sm text-gray-500">
                  Select district, code, financial year and date
                </p>
              </div>

              <button
                onClick={() => setReportModalOpen(false)}
                className="text-gray-500"
              >
                ✕
              </button>

            </div>

            <div className="p-6 space-y-5">

              {/* Financial Year */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Financial Year
                </label>

                <select
                  value={reportFilters.financialYear}
                  onChange={(e) => {

                    const fy = e.target.value;

                    const start =
                      Number(fy.substring(0, 4));

                    setReportFilters(prev => ({
                      ...prev,
                      financialYear: fy,
                      fromDate:
                        `${start}-04-01`,
                      toDate:
                        `${start + 1}-03-31`
                    }));

                  }}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="2026-27">
                    2026-27
                  </option>

                  <option value="2025-26">
                    2025-26
                  </option>
                </select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium mb-1">
                    From Date
                  </label>

                  <input
                    type="date"
                    value={reportFilters.fromDate}
                    onChange={(e) =>
                      setReportFilters(prev => ({
                        ...prev,
                        fromDate: e.target.value
                      }))
                    }
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    To Date
                  </label>

                  <input
                    type="date"
                    value={reportFilters.toDate}
                    onChange={(e) =>
                      setReportFilters(prev => ({
                        ...prev,
                        toDate: e.target.value
                      }))
                    }
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

              </div>

              {/* Scheme */}
              <div>

                <label className="block text-sm font-medium mb-1">
                  Scheme
                </label>

                <select
                  value={reportFilters.scheme}
                  onChange={(e) =>
                    setReportFilters(prev => ({
                      ...prev,
                      scheme: e.target.value
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2"
                >

                  <option value="">
                    All Schemes
                  </option>

                  {visibleSchemes.map(scheme => (
                    <option
                      key={scheme}
                      value={scheme}
                    >
                      {scheme}
                    </option>
                  ))}

                </select>

              </div>

              {/* District Selection */}
              <div>

                <div className="flex justify-between mb-2">

                  <label className="text-sm font-medium">
                    District / Center Code
                  </label>

                  <button
                    type="button"
                    onClick={() =>
                      setReportFilters(prev => ({
                        ...prev,
                        districts: []
                      }))
                    }
                    className="text-sm text-blue-600"
                  >
                    All Districts
                  </button>

                </div>

                <div className="border rounded-md max-h-64 overflow-y-auto">

                  <label className="flex items-center gap-3 px-4 py-3 bg-blue-50 border-b">

                    <input
                      type="checkbox"
                      checked={
                        reportFilters.districts.length === 0
                      }
                      onChange={() =>
                        setReportFilters(prev => ({
                          ...prev,
                          districts: []
                        }))
                      }
                    />

                    <span className="font-medium">
                      All Districts
                    </span>

                  </label>

                  {balanceData.map((dept, index) => {

                    const code =
                      dept?.type
                        ? dept.type.toUpperCase()
                        : '';

                    if (!code) return null;

                    return (

                      <label
                        key={`${code}-${index}`}
                        className="flex items-center gap-3 px-4 py-2 border-b hover:bg-gray-50"
                      >

                        <input
                          type="checkbox"
                          checked={
                            reportFilters.districts.includes(code)
                          }
                          onChange={() =>
                            toggleReportDistrict(code)
                          }
                        />

                        <span className="text-sm">

                          <strong>
                            {code}
                          </strong>

                          {' - '}

                          {dept.name || 'N/A'}

                        </span>

                      </label>

                    );
                  })}

                </div>

              </div>

              {reportError && (
                <div className="bg-red-50 text-red-700 border border-red-200 p-3 rounded">
                  {reportError}
                </div>
              )}

              <button
                type="button"
                onClick={downloadLedgerReport}
                disabled={reportLoading}
                className="w-full bg-green-600 hover:bg-green-700 text-white rounded-md px-4 py-3 disabled:bg-green-300"
              >
                {reportLoading
                  ? 'Preparing Excel...'
                  : 'Download Excel Report'}
              </button>

            </div>

          </div>
        </div>
      )}

      {/* Quarter-wise Allocation History Modal */}
      {!selectedDept && allocationView.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
          onClick={closeAllocationHistoryModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Quarter-wise Allocation History
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Select a center and scheme to view ledger history.
                </p>
              </div>

              <button
                type="button"
                onClick={closeAllocationHistoryModal}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full"
                title="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Filters */}
            <div className="px-6 py-4 border-b border-gray-200 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Center
                  </label>
                  <select
                    value={allocationFilters.centerCode}
                    onChange={(e) => {
                      setAllocationFilters((prev) => ({
                        ...prev,
                        centerCode: e.target.value
                      }));
                      setAllocationHistoryError('');
                      clearAllocationHistoryResults();
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Select Center</option>
                    {balanceData.map((dept, index) => {
                      const code = dept?.type ? dept.type.toUpperCase() : '';
                      if (!code) return null;
                      return (
                        <option key={`${code}-${index}`} value={code}>
                          {code} - {dept.name || 'N/A'}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Scheme / Fund Type
                  </label>
                  <select
                    value={allocationFilters.scheme}
                    onChange={(e) => {
                      setAllocationFilters((prev) => ({
                        ...prev,
                        scheme: e.target.value
                      }));
                      setAllocationHistoryError('');
                      clearAllocationHistoryResults();
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Select Scheme</option>
                    {visibleSchemes.map((scheme) => (
                      <option key={scheme} value={scheme}>
                        {getSchemeDisplayName(scheme)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Financial Year
                  </label>
                  <select
                    value={allocationFilters.financialYear}
                    onChange={(e) => {
                      setAllocationFilters((prev) => ({
                        ...prev,
                        financialYear: e.target.value
                      }));
                      clearAllocationHistoryResults();
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="2026-27">2026-27</option>
                    <option value="2025-26">2025-26</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Quarter
                  </label>
                  <select
                    value={allocationFilters.quarter}
                    onChange={(e) => {
                      setAllocationFilters((prev) => ({
                        ...prev,
                        quarter: e.target.value
                      }));
                      clearAllocationHistoryResults();
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    {quarterOptions.map((quarter) => (
                      <option key={quarter.value} value={quarter.value}>
                        {quarter.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => fetchAllocationHistory(null, '', allocationFilters)}
                    disabled={allocationHistoryLoading}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                  >
                    {allocationHistoryLoading ? 'Loading...' : 'Search'}
                  </button>
                </div>
              </div>

              {allocationHistoryError && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {allocationHistoryError}
                </div>
              )}
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {allocationView.centerCode && allocationView.scheme ? (
                <>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {allocationView.centerCode} - {allocationView.centerName}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {allocationView.centerType || 'Center'} | {allocationView.scheme}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        closeAllocationHistoryModal();
                        fetchDepartmentTransactions(
                          `${allocationView.centerCode}-${allocationView.scheme}`
                        );
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200"
                    >
                      View Full Transactions
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <p className="text-sm text-blue-800">Opening Balance</p>
                      <p className="text-xl font-bold text-blue-600">
                        {formatCurrency(allocationView.totals.opening_balance)}
                      </p>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                      <p className="text-sm text-green-800">Allocated / Added Fund</p>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(allocationView.totals.added_amount)}
                      </p>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                      <p className="text-sm text-red-800">Used Amount</p>
                      <p className="text-xl font-bold text-red-600">
                        {formatCurrency(allocationView.totals.used_amount)}
                      </p>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                      <p className="text-sm text-purple-800">Closing Balance</p>
                      <p className="text-xl font-bold text-purple-600">
                        {formatCurrency(allocationView.totals.closing_balance)}
                      </p>
                    </div>
                  </div>

                  <h4 className="text-md font-medium text-gray-700 mb-3">
                    Date-wise History
                  </h4>

                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Particulars
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Credit / Added
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Debit / Used
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Balance
                          </th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {allocationHistoryLoading ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              Loading history...
                            </td>
                          </tr>
                        ) : allocationView.history.length > 0 ? (
                          allocationView.history.map((item, index) => (
                            <tr key={item.id || `${item.date}-${index}`} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {formatDate(item.date)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.particulars || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-green-600 font-medium whitespace-nowrap">
                                {formatCurrency(item.cr)}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-red-600 font-medium whitespace-nowrap">
                                {formatCurrency(item.dr)}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700 font-medium whitespace-nowrap">
                                {formatCurrency(item.balance)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              No history data available for selected quarter
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="py-14 text-center text-gray-500">
                  <svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-medium">Choose Center and Scheme</p>
                  <p className="text-sm mt-1">Then click Search to view quarter-wise history.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      {selectedDept && (
        <>
        
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                {selectedDept.toUpperCase()} Transactions
              </h2>
            </div>
            
            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
              {/* Show Allocate Fund input and button only if userRole is department */}
           {/* Show Allocate Fund input and button for department OR for state/regional users viewing GOI-MoRD */}
{(userRole === 'department' || 
  ((userRole === 'state' || userRole === 'region') && selectedDept && selectedDept.includes('GOI-MoRD'))) && (
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 w-full md:w-auto">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Allocate Funds</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label htmlFor="allocationParticulars" className="block text-xs text-gray-600 mb-1">
                        Particulars
                      </label>
                      <input
                        id="allocationParticulars"
                        type="text"
                        value={allocationParticulars}
                        onChange={(e) => setAllocationParticulars(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full"
                        placeholder="Enter particulars"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="allocationAmount" className="block text-xs text-gray-600 mb-1">
                        Amount (₹)
                      </label>
                      <input
                        id="allocationAmount"
                        type="number"
                        value={allocationAmount}
                        onChange={(e) => setAllocationAmount(Number(e.target.value))}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full"
                        min="1"
                        placeholder="Enter amount"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={allocateFunds}
                    disabled={allocateLoading || !allocationAmount || !allocationParticulars}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {allocateLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Allocating...
                      </>
                    ) : (
                      'Allocate Fund'
                    )}
                  </button>
                </div>
              )}
              
              <button
                onClick={() => {
                  setSelectedDept(null);
                  setTransactions([]);
                  setAllocateSuccess(false);
                  clearFilters();
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-center w-full md:w-auto"
              >
                Back to Departments
              </button>
            </div>
          </div>

          {/* Transaction Search and Filter */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Search & Filter Transactions</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Text Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search particulars..."
                  value={transactionSearchTerm}
                  onChange={(e) => setTransactionSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg 
                  className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Date From */}
              <div>
                <label htmlFor="dateFrom" className="block text-xs text-gray-600 mb-1">From Date</label>
                <input
                  id="dateFrom"
                  type="date"
                  value={dateFilter.from}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>

              {/* Date To */}
              <div>
                <label htmlFor="dateTo" className="block text-xs text-gray-600 mb-1">To Date</label>
                <input
                  id="dateTo"
                  type="date"
                  value={dateFilter.to}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Success message */}
          {allocateSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md mb-4">
              <p className="flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Funds successfully allocated to {selectedDept.toUpperCase()} department.
              </p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-700">
                Transaction History 
                {filteredTransactions.length !== transactions.length && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({filteredTransactions.length} of {transactions.length} transactions)
                  </span>
                )}
              </h3>
            </div>

            {filteredTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Particulars
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransactions.map((transaction, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(transaction.date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {transaction.particulars}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          {transaction.cr && transaction.cr !== "0.00" ? (
                            <span className="text-green-600 font-medium">
                              {formatCurrency(transaction.cr)}
                            </span>
                          ) : transaction.dr && transaction.dr !== "0.00" ? (
                            <span className="text-red-600 font-medium">
                              {formatCurrency(transaction.dr)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                          {formatCurrency(transaction.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                {transactionSearchTerm || dateFilter.from || dateFilter.to ? 
                  'No transactions found matching the current filters.' : 
                  'No transaction records found.'
                }
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <p className="text-sm text-green-800">As per PFMS</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    filteredTransactions.reduce((sum, t) => sum + (parseFloat(t.cr) || 0), 0)
                  )}
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                <p className="text-sm text-red-800">As per Administrative Sanctioned</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(
                    filteredTransactions.reduce((sum, t) => sum + (parseFloat(t.dr) || 0), 0)
                  )}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-800">Current Balance</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(currentBalance)}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DepartmentLedger;